import React, { useContext, useEffect, useMemo, useState } from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import {
  ar,
  bn,
  da,
  de,
  el,
  en,
  es,
  et,
  fr,
  gu,
  hi,
  hu,
  id,
  it,
  ja,
  ko,
  ms,
  nb,
  nl,
  nn,
  no,
  pl,
  pt,
  ro,
  ru,
  sv,
  th,
  tl,
  tr,
  uk,
  ur,
  vi,
  zh,
} from 'make-plural/plurals';
import { messages as enMessages } from 'locales/en/messages';

export type Locale =
  | 'ar'
  | 'bn'
  | 'da'
  | 'de'
  | 'el'
  | 'en'
  | 'es'
  | 'et'
  | 'fr'
  | 'gu'
  | 'hi'
  | 'hu'
  | 'id'
  | 'it'
  | 'ja'
  | 'ko'
  | 'ms'
  | 'nb'
  | 'nl'
  | 'nn'
  | 'no'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'ru'
  | 'sv'
  | 'th'
  | 'tl'
  | 'tr'
  | 'uk'
  | 'ur'
  | 'vi'
  | 'zh';

export const locales: Record<Locale, string> = {
  ar: 'العربية',
  bn: 'বাংলা',
  da: 'Dansk',
  de: 'Deutsch',
  el: 'Ελληνικά',
  en: 'English',
  es: 'Español',
  et: 'Eesti',
  fr: 'Français',
  gu: 'ગુજરાતી',
  hi: 'हिन्दी',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  ms: 'Bahasa Melayu',
  nb: 'Norsk Bokmål',
  nl: 'Nederlands',
  nn: 'Nynorsk',
  no: 'Norsk',
  pl: 'Polski',
  pt: 'Português',
  ro: 'Română',
  ru: 'Русский',
  sv: 'Svenska',
  th: 'ไทย',
  tl: 'Tagalog',
  tr: 'Türkçe',
  uk: 'Українська',
  ur: 'اردو',
  vi: 'Tiếng Việt',
  zh: '中文',
};
export const defaultLocale: Locale = 'en';

/**
 * Detects the browser language and returns a supported locale or the default fallback
 */
const detectBrowserLocale = (): Locale => {
  // Get browser language (e.g., 'en-US', 'pl-PL')
  const browserLang = navigator.language || navigator.languages?.[0];

  if (!browserLang) {
    return defaultLocale;
  }

  // Extract language code (e.g., 'en-US' -> 'en', 'pl-PL' -> 'pl')
  let langCode = browserLang.split('-')[0].toLowerCase();

  // Filipino browsers report 'fil' or 'tl'; our catalog uses 'tl'
  if (langCode === 'fil') {
    langCode = 'tl';
  }

  // Check if the detected language is supported
  if (langCode in locales) {
    return langCode as Locale;
  }

  // Fallback to default locale
  return defaultLocale;
};

/**
 * Dynamically loads and activates a locale
 */
export const i18nDynamicActivate = async (locale: Locale) => {
  const { messages } = await import(`../../locales/${locale}/messages`);
  i18n.load(locale, messages);
  i18n.activate(locale);
};

// Load locale data and plural rules for all supported languages
i18n.loadLocaleData({
  ar: { plurals: ar },
  bn: { plurals: bn },
  da: { plurals: da },
  de: { plurals: de },
  el: { plurals: el },
  en: { plurals: en },
  es: { plurals: es },
  et: { plurals: et },
  fr: { plurals: fr },
  gu: { plurals: gu },
  hi: { plurals: hi },
  hu: { plurals: hu },
  id: { plurals: id },
  it: { plurals: it },
  ja: { plurals: ja },
  ko: { plurals: ko },
  ms: { plurals: ms },
  nb: { plurals: nb },
  nl: { plurals: nl },
  nn: { plurals: nn },
  no: { plurals: no },
  pl: { plurals: pl },
  pt: { plurals: pt },
  ro: { plurals: ro },
  ru: { plurals: ru },
  sv: { plurals: sv },
  th: { plurals: th },
  tl: { plurals: tl },
  tr: { plurals: tr },
  uk: { plurals: uk },
  ur: { plurals: ur },
  vi: { plurals: vi },
  zh: { plurals: zh },
});

// Load only English messages by default
i18n.load('en', enMessages);

// Detect browser language
const initialLocale = detectBrowserLocale();

// If browser locale is English, activate it immediately
// Otherwise, dynamically load and activate the detected locale
if (initialLocale === 'en') {
  i18n.activate(initialLocale);
} else {
  // Load messages first, then activate (handled by i18nDynamicActivate)
  i18nDynamicActivate(initialLocale);
}

export interface LocaleContextState {
  locale: Locale;
  setLocale: React.Dispatch<React.SetStateAction<Locale>>;
}

const defaultState: LocaleContextState = {
  locale: initialLocale,
  setLocale: () => undefined,
};

export const LocaleContext = React.createContext(defaultState);
export const useLocale = () => useContext(LocaleContext);

export const LocaleProvider = ({ children }: { children: React.ReactNode }) => {
  const [locale, setLocale] = useState(defaultState.locale);

  useEffect(() => {
    // Only activate if locale has changed from the initial locale
    if (locale !== initialLocale) {
      i18nDynamicActivate(locale);
    }
  }, [locale]);

  const state = useMemo(
    () => ({
      locale,
      setLocale,
    }),
    [locale]
  );

  return (
    <I18nProvider i18n={i18n}>
      <LocaleContext.Provider value={state}>{children}</LocaleContext.Provider>
    </I18nProvider>
  );
};
